import React from 'react';
import styles from './Button.module.css';
import Icon from './Icon';

export interface ButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: React.ReactNode;
  /**
   * Visual style. Names follow FIGMA, not the kit.
   *
   * As of 2026-07-22 (acuerdo con Visibilidad) buttons follow Figma's naming:
   * `primary` is the main call to action = solid blue. This is the INTUITIVE
   * meaning — but note the kit's own class names are crossed, so the class emitted
   * to the DOM has the OPPOSITE name. If you inspect the DOM, don't be alarmed:
   *
   *   variant="primary"   -> emits kit .btn-secondary = solid blue   (Figma "primary blue")
   *   variant="secondary" -> emits kit .btn-primary   = white/outline (Figma "secondary")
   *   cta                 -> emits kit .btn-cta        = solid yellow  (Figma "primary yellow")
   *   ghost               -> emits kit .btn-primary (white/outline), NOT the kit's
   *                          .btn-ghost. The kit's .btn-ghost is the dark translucent
   *                          style for image backgrounds ("secondary alpha" in Figma)
   *                          and is not exposed here. (Unchanged by the 2026-07-22 swap.)
   *   text                -> no kit class at all; styled entirely by Button.module.css (.text).
   *
   * See `kitClassByVariant` below for the full variant -> kit-class map.
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'cta'
    | 'featured'
    | 'listed'
    | 'ghost'
    | 'inline'
    | 'text'
    | 'iconOnly'
    | 'danger';
  size?: 'default' | 'sm';
  icon?: string;
  iconPos?: 'left' | 'right';
  iconShape?: 'rounded';
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  style?: React.CSSProperties;
  /** Text shown while `loading` is true. Defaults to the Spanish "Cargando...". */
  loadingLabel?: string;
}

/**
 * Maps the Figma-named `variant` to the kit class emitted to the DOM.
 * primary/secondary are INVERTED on purpose (Figma names vs. kit names are crossed;
 * acuerdo con Visibilidad 2026-07-22): `primary` -> solid blue, `secondary` -> outline.
 * `ghost` deliberately reuses the kit's white/outline class and is NOT part of the swap.
 * Only variants that render a `btn-*` kit class appear here — iconOnly / danger / text
 * are handled separately above.
 */
const kitClassByVariant: Record<string, string> = {
  primary: 'btn-secondary', // Figma "primary" = solid blue  -> kit .btn-secondary
  secondary: 'btn-primary', // Figma "secondary" = white/outline -> kit .btn-primary
  cta: 'btn-cta',
  featured: 'btn-featured',
  listed: 'btn-listed',
  inline: 'btn-inline',
  ghost: 'btn-primary', // white/outline; unchanged by the swap
};

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'default',
  icon,
  iconPos = 'right',
  iconShape,
  disabled = false,
  loading = false,
  type,
  onClick,
  className = '',
  style,
  loadingLabel = 'Cargando...',
  ...restProps
}) => {
  const getButtonClasses = () => {
    if (variant === 'iconOnly') {
      const classes = [styles.iconOnly];
      if (disabled || loading) classes.push(styles.disabled);
      if (className) classes.push(className);
      return classes.join(' ');
    }

    if (variant === 'danger') {
      const classes = ['uc-btn', styles.button, styles.danger];
      if (size === 'sm') classes.push(styles.sm);
      if (disabled || loading) classes.push('btn-disabled', styles.disabled);
      if (className) classes.push(className);
      return classes.join(' ');
    }

    // `text` has no kit class — styles.text is the only thing painting it.
    const classes = ['uc-btn', styles.button];
    if (variant === 'text') {
      classes.push(styles.text);
    } else {
      classes.push(kitClassByVariant[variant]);
    }

    if (size === 'sm') {
      classes.push(styles.sm);
    }

    if (disabled || loading) {
      classes.push('btn-disabled', styles.disabled);
    }

    if (className) {
      classes.push(className);
    }

    return classes.join(' ');
  };

  const iconMarginClass =
    variant === 'iconOnly' || !children ? '' : iconPos === 'left' ? 'mr-2' : 'ml-2';
  const IconElement = icon ? (
    <Icon name={icon} shape={iconShape} className={iconMarginClass} />
  ) : null;

  const content = loading ? (
    loadingLabel
  ) : (
    <>
      {iconPos === 'left' && IconElement}
      {children}
      {iconPos === 'right' && IconElement}
    </>
  );

  // Render a native <button> when type is specified, so type="submit" works in forms
  if (type) {
    return (
      <button
        type={type}
        disabled={disabled || loading}
        className={getButtonClasses()}
        onClick={disabled || loading ? undefined : (onClick as any)}
        style={style}
        {...(restProps as any)}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      role='button'
      aria-disabled={disabled || loading}
      className={getButtonClasses()}
      onClick={disabled || loading ? undefined : onClick}
      style={style}
      {...restProps}
    >
      {content}
    </a>
  );
};

export default Button;
